import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Equipment, Location } from '@core/interfaces';
import { fromEvent, merge, Subject } from 'rxjs';
import { debounceTime, switchMap, takeUntil } from 'rxjs/operators';
import { sentry } from '../../../../../../../sentry';
import { TreeViewItem } from '../../../../../../core/components/fc-tree-view/fc-tree.types';
import { CustomMessagesService } from '../../../../../../core/errors/custom-messages.service';
import { LocationEnvironment } from '../../../../../../core/interfaces/locations-environments';
import { LocationSector } from '../../../../../../core/interfaces/locations-sectors';
import { delay } from '../../../../../../core/utils/delay';
import { EquipmentLinkService } from './services/equipment/equipment.service';
import { LocationLinkEnvironmentService } from './services/location-environment/location-environment.service';
import { LocationLinkSectorService } from './services/location-sector/location-sector.service';
import { LocationTabLinksService } from './tab-links.service';
import { LinkSectorsEnvironmentsEquipmentsParams } from './tab-links.types';

@Component({
  selector: 'tab-links',
  templateUrl: './tab-links.component.html',
  styleUrls: ['./tab-links.component.scss'],
})
export class TabLinksComponent implements OnDestroy, OnInit {

  @Input() location: Location;
  @Input() isViewMode: boolean;
  public readonly searchTerm = new FormControl('');
  private readonly destroy$ = new Subject<void>();
  public isEmpty = true;
  public isLoading = false;
  public locationLinks: TreeViewItem[] = [];
  public unlinkedEquipments: TreeViewItem[] = [];

  constructor(
    private readonly messageService: CustomMessagesService,
    private readonly locationLinksTabModalService: LocationTabLinksService,
    private readonly locationSectorService: LocationLinkSectorService,
    private readonly locationEnvironmentService: LocationLinkEnvironmentService,
    private readonly equipmentService: EquipmentLinkService
  ) {}

  async openCreateSectorDialog(): Promise<void> {
    await this.locationSectorService.openCreateLocationSectorDialog({ location: this.location });
  }

  async openCreateEquipmentDialog(): Promise<void> {
    await this.equipmentService.openCreateEquipmentDialog({ location: this.location });
  }

  async openCreateEnvironmentDialog(): Promise<void> {
    await this.locationEnvironmentService.openCreateLocationEnvironmentDialog({
      location: this.location,
      locationSectorId: null
    });

  }

  public async ngOnInit(): Promise<void> {

    merge(
      this.searchTerm.valueChanges.pipe(debounceTime(1000)),
      fromEvent(window, 'location-links::reload-tree-view')
    ).pipe(
      takeUntil(this.destroy$),
      switchMap(() => this.loadLocationLinks())
    ).subscribe();

    window.emitEvent('location-links::reload-tree-view');
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private async loadLocationLinks(): Promise<void> {
    this.isLoading = true;

    const [locationSectors, locationEnvironments, equipments] = await Promise.all([
      this.searchLocationSectors(),
      this.searchLocationEnvironments(),
      this.searchEquipments(),
      delay(500)
    ]).catch((error) => {
      this.messageService.error('UNKNOWN_ERROR');
      sentry.captureException(error);
      return [[], [], []];
    });

    await this.populateLocationLinks({
      sectors: locationSectors,
      environments: locationEnvironments,
      equipments: equipments
    });

    this.isLoading = false;
  }

  private async searchLocationSectors(): Promise<LocationSector[]> {
    return this.locationLinksTabModalService.searchLocationSectors({
      locationId: this.location.id,
    });
  }

  private async searchLocationEnvironments(): Promise<LocationEnvironment[]> {
    return this.locationLinksTabModalService.searchLocationEnvironments({
      locationId: this.location.id,
    });
  }

  private async searchEquipments(): Promise<Equipment[]> {
    return this.locationLinksTabModalService.searchEquipments({
      locationId_in: [this.location.id],
      name_or_number_or_sector_or_environment_contains: this.searchTerm.value.trim().toLowerCase(),
    });
  }


  private async populateLocationLinks(params: LinkSectorsEnvironmentsEquipmentsParams): Promise<void> {
    const { sectors, equipments } = this.locationLinksTabModalService.linkSectorsEnvironmentsEquipments({
      sectors: params.sectors,
      environments: params.environments,
      equipments: params.equipments
    });

    this.locationLinks = await this.locationLinksTabModalService.formatSectors(sectors)
      .then((items) => this.filter(items, this.searchTerm.value.trim().toLowerCase()));

    this.unlinkedEquipments = await this.locationLinksTabModalService.formatUnlinkedEquipments(equipments)
      .then((items) => this.filter(items, this.searchTerm.value.trim().toLowerCase()));

    this.isEmpty = !this.locationLinks.length && !this.unlinkedEquipments.length;
  }

  private filter(items: TreeViewItem[], searchTerm: string): TreeViewItem[] {
    return items.filter((item) => {
      if (!searchTerm) { return true; }

      if (item.children) {
        item.children = this.filter(item.children, searchTerm);
      }

      return item.searchTerm.includes(searchTerm) || (item.children && item.children.length) || this.parentMatch(item, searchTerm);
    });
  }

  private parentMatch(item: TreeViewItem, searchTerm: string): boolean {
    if (!item.parent) { return false; }


    return item.parent.searchTerm.includes(searchTerm) || this.parentMatch(item.parent, searchTerm);
  }

}
